import React, { useState, useEffect, useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default function InvoiceList() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination State
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  // Selected invoice for viewing line items
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Ref for PDF generation container
  const pdfRef = useRef();

  const fetchInvoices = async (currentPage) => {
    setLoading(true);
    try {
      const response = await fetch(`https://cloudapi.emedhatechnologies.com/pages/api/api/invoicesvsm?page=${currentPage}&limit=${limit}`);
      const result = await response.json();

      if (result.success) {
        setInvoices(result.data);
        setTotalPages(result.pagination.totalPages);
      } else {
        setError(result.message || "Failed to load invoices.");
      }
    } catch (err) {
      console.error("Network error:", err);
      setError("Failed to connect to the server.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices(page);
  }, [page]);

  const handleDownloadPDF = async () => {
    const input = pdfRef.current;
    if (!input) return;

    try {
      const canvas = await html2canvas(input, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 10, pdfWidth, pdfHeight);
      pdf.save(`invoice_${selectedInvoice.invoice_number || selectedInvoice.id}.pdf`);
    } catch (err) {
      console.error("Failed to generate PDF:", err);
    }
  };

  if (loading && invoices.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: '#666', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
        Loading invoices...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', fontSize: '12px', color: '#e53e3e', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
        Error: {error}
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%', 
      margin: '0', 
      padding: '0', 
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
      fontSize: '12px',
      color: '#2d3748'
    }}>
      {/* Header */}
      <div style={{ marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h2 style={{ fontSize: '15px', fontWeight: '600', letterSpacing: '-0.3px', color: '#1a202c', margin: 0 }}>
          Invoices Directory
        </h2>
        <span style={{ fontSize: '11px', color: '#718096' }}>Page {page} of {totalPages || 1}</span>
      </div>
      
      {/* Table Wrapper for Horizontal Responsiveness */}
      <div style={{ 
        background: '#ffffff', 
        border: '1px solid #e2e8f0', 
        borderRadius: '6px', 
        boxShadow: '0 1px 2px rgba(0,0,0,0.01)',
        overflowX: 'auto' 
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '700px' }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <th style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Inv #</th>
              <th style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Customer</th>
              <th style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Date</th>
              <th style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Items</th>
              <th style={{ padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: '#4a5568', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: 'center', padding: '24px', color: '#a0aec0' }}>No invoices found.</td>
              </tr>
            ) : (
              invoices.map((inv) => (
                <tr key={inv.id} style={{ borderBottom: '1px solid #edf2f7', transition: 'background 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f7fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                  <td style={{ padding: '8px 12px', fontWeight: '500', color: '#2d3748' }}>{inv.invoice_number || `#${inv.id}`}</td>
                  <td style={{ padding: '8px 12px', color: '#4a5568' }}>
                    <div style={{ fontWeight: '500' }}>{inv.customer_name}</div>
                    <div style={{ fontSize: '11px', color: '#718096' }}>{inv.email}</div>
                  </td>
                  <td style={{ padding: '8px 12px', color: '#4a5568' }}>{new Date(inv.invoice_date).toLocaleDateString()}</td>
                  <td style={{ padding: '8px 12px' }}>
                    <span style={{ background: '#edf2f7', color: '#4a5568', padding: '2px 6px', borderRadius: '10px', fontSize: '11px', fontWeight: '500' }}>
                      {inv.items ? inv.items.length : 0} items
                    </span>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    <button 
                      onClick={() => setSelectedInvoice(inv)}
                      style={{ 
                        background: '#ffffff', 
                        border: '1px solid #cbd5e0', 
                        color: '#2d3748', 
                        padding: '3px 8px', 
                        fontSize: '11px', 
                        borderRadius: '4px', 
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
        <button 
          disabled={page <= 1} 
          onClick={() => setPage(prev => Math.max(prev - 1, 1))}
          style={{ 
            background: page <= 1 ? '#f7fafc' : '#ffffff',
            border: '1px solid #e2e8f0',
            color: page <= 1 ? '#a0aec0' : '#4a5568',
            padding: '4px 10px',
            fontSize: '11px',
            borderRadius: '4px',
            cursor: page <= 1 ? 'not-allowed' : 'pointer',
            fontWeight: '500'
          }}
        >
          Previous
        </button>
        <span style={{ fontSize: '11px', color: '#718096' }}>Page <strong>{page}</strong> of {totalPages || 1}</span>
        <button 
          disabled={page >= totalPages} 
          onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
          style={{ 
            background: page >= totalPages ? '#f7fafc' : '#ffffff',
            border: '1px solid #e2e8f0',
            color: page >= totalPages ? '#a0aec0' : '#4a5568',
            padding: '4px 10px',
            fontSize: '11px',
            borderRadius: '4px',
            cursor: page >= totalPages ? 'not-allowed' : 'pointer',
            fontWeight: '500'
          }}
        >
          Next
        </button>
      </div>

      {/* Detailed Modal mapping all invoice fields and line items */}
      {selectedInvoice && (
        <div style={{ 
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', 
          background: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(2px)', 
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000,
          padding: '12px'
        }}>
          <div style={{ 
            background: '#ffffff', 
            padding: '20px', 
            borderRadius: '8px', 
            width: '100%', 
            maxWidth: '560px', 
            maxHeight: '85vh', 
            overflowY: 'auto',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            border: '1px solid #e2e8f0'
          }}>
            {/* Target Ref Container for PDF Export */}
            <div ref={pdfRef} style={{ background: '#ffffff', padding: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1a202c', margin: 0 }}>
                    Invoice {selectedInvoice.invoice_number || `#${selectedInvoice.id}`}
                  </h3>
                  <span style={{ fontSize: '11px', color: '#718096' }}>Created: {new Date(selectedInvoice.created_at || selectedInvoice.invoice_date).toLocaleString()}</span>
                </div>
                <button 
                  onClick={() => setSelectedInvoice(null)}
                  style={{ background: 'none', border: 'none', fontSize: '15px', color: '#a0aec0', cursor: 'pointer', padding: '0' }}
                >
                  ✕
                </button>
              </div>

              {/* Customer & Billing Meta Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', background: '#f8fafc', padding: '10px', borderRadius: '6px', border: '1px solid #edf2f7', marginBottom: '14px' }}>
                <div>
                  <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#718096', fontWeight: '600' }}>Customer</div>
                  <div style={{ fontWeight: '500', color: '#2d3748' }}>{selectedInvoice.customer_name}</div>
                  <div style={{ color: '#4a5568' }}>{selectedInvoice.email}</div>
                  <div style={{ color: '#4a5568' }}>{selectedInvoice.phone}</div>
                </div>
                <div>
                  <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#718096', fontWeight: '600' }}>Addresses & Info</div>
                  <div style={{ color: '#4a5568' }}><strong>Billing:</strong> {selectedInvoice.billing_address}</div>
                  <div style={{ color: '#4a5568' }}><strong>Shipping:</strong> {selectedInvoice.shipping_address}</div>
                  {selectedInvoice.gstin && <div style={{ color: '#4a5568' }}><strong>GSTIN:</strong> {selectedInvoice.gstin}</div>}
                </div>
              </div>

              {/* Line Items List */}
              <div style={{ borderTop: '1px solid #edf2f7', paddingTop: '10px' }}>
                <h4 style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#718096', margin: '0 0 8px 0' }}>Line Items</h4>
                
                {selectedInvoice.items && selectedInvoice.items.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {selectedInvoice.items.map((item, idx) => (
                      <div key={idx} style={{ 
                        background: '#ffffff', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' 
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                          <span style={{ fontWeight: '500', color: '#2d3748' }}>{item.description}</span>
                          <span style={{ fontWeight: '600', color: '#2d3748' }}>${Number(item.amount).toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#718096' }}>
                          <span>Cut Dimensions: {item.cut_dimensions}</span>
                          <span>Qty: {item.qty} × ${Number(item.rate).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#a0aec0', textAlign: 'center', padding: '12px 0', margin: 0 }}>No line items associated with this invoice.</p>
                )}
              </div>
            </div>

            {/* Modal Actions */}
            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button 
                onClick={handleDownloadPDF}
                style={{ 
                  padding: '5px 12px', 
                  background: '#2b6cb0', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: '4px', 
                  fontSize: '11px', 
                  cursor: 'pointer',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                ↓ Download PDF
              </button>
              <button 
                onClick={() => setSelectedInvoice(null)}
                style={{ 
                  padding: '5px 12px', 
                  background: '#1a202c', 
                  color: '#fff', 
                  border: 'none', 
                  borderRadius: '4px', 
                  fontSize: '11px', 
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}